import { clsx } from 'clsx';
import { useController, useWatch } from 'react-hook-form';
import React, { useEffect, useState } from 'react';
import { SimpleForm, useNotify, useRefresh, useResourceContext } from 'react-admin';
import {
  AntdDateInput,
  FormRow,
  SelectInput,
  SelectArrayInput,
  TextInput, SelectedPhoneInput, SwitchInput,
} from '@components';
import {
  State,
  DealerType,
  dealerTiersChoices,
  AdminOptionCats,
  Crop,
  dealerPaymentType,
  dealerSupplierLoanChoices,
} from '@types';
import { useRecord } from '@hooks';
import { axiosErrorToString } from '@utils';
import { Actions } from './DealerFormActions';
import { DealerContacts } from './DealerContacts';
import { UniqueCrops } from './DealerUniqueCrops';
import { VerqorRRInput } from './DealerFormVerqorRR';
import { financeSettingsHttp } from '@network/finance-settings-http';
import { Footer } from '@pages/dealer/BuyerCreator/components/Footer';
import { stateHttp, adminSettingsHttp, adminHttp, dealerHttp, cropHttp } from '@network';
import cls from './DealerForm.module.css';

export interface Option {
  id: string | number;
  name: string;
}

interface FormProps {
  canEdit?: boolean;
  editPage?: boolean;
}

const validateRFC = (value: string | undefined, num: number) => {
  if (!value) {
    return undefined;
  }

  const length = value.length;
  if (length < num || length > num) {
    return `RFC must contain ${num} characters`;
  }

  return undefined;
};

const validateLegalRep = (value: string | undefined, flag: boolean) => {
  if (!value) {
    return undefined;
  }

  if (!flag && value) {
    return 'Legal Rep must be empty';
  }

  return undefined;
};

const validateCIEC = (value: string | undefined) => {
  if (!value) {
    return undefined;
  }

  const length = value.length;
  if (length < 8) {
    return 'RFC must contain 8 characters';
  }

  return undefined;
};

const validateURL = (value: string | undefined) => {
  if (!value) {
    return undefined;
  }

  const urlPattern = /^[a-zA-Z0-9\-._:/?#[\]@!$&'()*+,;=%]*$/;

  if (!urlPattern.test(value)) {
    return 'URL can only contain Latin letters, numbers, and basic URL characters';
  }

  const domainPattern = /^(https?:\/\/)?([a-zA-Z0-9]([a-zA-Z0-9\-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}/;
  const valueWithoutProtocol = value.replace(/^https?:\/\//, '');

  if (!domainPattern.test(value) || !valueWithoutProtocol.includes('.')) {
    return 'Please enter a valid domain name';
  }

  try {
    const urlToValidate = value.includes('://') ? value : `https://${value}`;
    const url = new URL(urlToValidate);

    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
      return 'Please enter a valid URL';
    }

    return undefined;
  } catch {
    return 'Please enter a valid URL';
  }
};

const PhoneFieldConnector = () => {
  const {
    field: { onChange, value },
  } = useController({ name: 'phone', defaultValue: '' });

  return (
    <SelectedPhoneInput
      value={value}
      onChange={onChange}
      placeholder="(000) 000-0000"
      className={cls.phoneInput}
      name="phone"
    />
  );
};

const RFCFieldConnector = () => {
  const isCompany = useWatch({ name: 'isCompany' });

  return (
    <TextInput
      source="rfc"
      label={false}
      inputProps={{ maxLength: isCompany ? 12 : 13 }}
      validate={val => validateRFC(val, isCompany ? 12 : 13)}
      format={(v: string | undefined) => (v || '').toUpperCase()}
    />
  );
};

const LegalRepConnector = () => {
  const isCompany = useWatch({ name: 'isCompany' });
  const type = useWatch({ name: 'type' });

  return (
    <TextInput
      source="legalRep"
      label={false}
      inputProps={{ maxLength: 100 }}
      disabled={!isCompany && type !== 'associate'}
      validate={val => validateLegalRep(val, type !== 'associate' || isCompany)}
    />
  );
};

export const DealerForm = ({ canEdit, editPage }: FormProps) => {
  const resource = useResourceContext();
  const record = useRecord();
  const notify = useNotify();
  const refresh = useRefresh();

  const dealerType = resource === 'dealer' ? DealerType.BUYER
    : resource === 'dealer-retailer' ? DealerType.RETAILER : DealerType.ASSOCIATE;

  const [states, setStates] = useState<State[]>([]);
  const [statuses, setStatuses] = useState<Option[]>([]);
  const [owners, setOwners] = useState<Option[]>([]);
  const [types, setTypes] = useState<Option[]>([]);
  const [sources, setSources] = useState<Option[]>([]);
  const [crops, setCrops] = useState<Crop[]>([]);
  const [activations, setActivations] = useState<Option[]>([]);

  const mainColGrid = {
    xxl: { title: 5, children: 19 },
    sm: { title: 10, children: 14 },
    xs: { title: 24, children: 24 },
  };

  useEffect(() => {
    (async () => {
      const typeCategory = dealerType === DealerType.BUYER
        ? AdminOptionCats.PARTNERS_TYPE_BUYER
        : dealerType === DealerType.RETAILER
          ? AdminOptionCats.PARTNERS_TYPE_RETAILER
          : AdminOptionCats.PARTNERS_TYPE_ASSOCIATE;

      const [
        allStates,
        dealerStatusesObjects,
        dealerTypesObjects,
        ownersList,
        sourcesList,
        allCrops,
        allActivations,
      ] = await Promise.all([
        stateHttp.getAll(),
        adminSettingsHttp.getOptionsByCategory(AdminOptionCats.PARTNERS_STATUS),
        adminSettingsHttp.getOptionsByCategory(typeCategory),
        adminHttp.getResponsibleOwners(),
        dealerHttp.getSourceOptions(),
        cropHttp.getAll(),
        financeSettingsHttp.getActivations(),
      ]);

      setStates(allStates);
      setStatuses((dealerStatusesObjects || []).map(item => ({
        id: item.id as number,
        name: item.name,
      })));
      setTypes(dealerTypesObjects.map(item => ({
        id: item.id as number,
        name: item.name,
      })));
      setOwners(ownersList.map(item => ({
        id: item.id,
        name: item.name,
      })));
      setSources(sourcesList.map(item => ({
        id: item.value,
        name: item.label,
      })));
      setCrops(allCrops);
      setActivations((allActivations || []).map(item => ({
        id: item.id,
        name: item.activation,
      })));
    })();
  }, [dealerType]);

  const salesforceSync = async () => {
    if (!record?.id) return;

    try {
      await dealerHttp.salesforceSync(Number(record.id));
      notify('Salesforce sync completed successfully', { type: 'success' });
    } catch (e) {
      notify(axiosErrorToString(e), { type: 'error' });
    }
  };

  const onDeleteAccount = async () => {
    if (!record?.id) return;

    try {
      await dealerHttp.deleteAccount(Number(record.id));
      notify('Account was deleted', { type: 'success' });
      refresh();
    } catch (e) {
      notify(axiosErrorToString(e), { type: 'error' });
    }
  };

  return (
    <div className={cls.wrap}>
      <SimpleForm
        className={clsx(cls.form, 'manager-form')}
        toolbar={<Actions
          resource={resource}
          canEdit={canEdit}
          editPage={editPage}
          onSalesforceSync={salesforceSync}
          onDeleteAccount={onDeleteAccount}
          record={record}
        />}
      >
        <div className={cls.floatField}>
          <div className={cls.floatFieldRow}>
            <div className={cls.floatFieldCol}>
              Partner Type:
            </div>
            <div className={cls.floatFieldCol}>
              <div className={cls.floatFieldWrap}>
                PFAE<SwitchInput className={cls.floatSwitcher} source="isCompany" />PM
              </div>
            </div>
          </div>
        </div>

        <div className={cls.id}>Code: {record?.code ?? '—'}</div>
        <div className={cls.info}>
          <div className={cls.col}>
            <FormRow
              title="Trade Name:"
              width={mainColGrid}
              rowClassName={clsx(cls.formRow, cls.mainFormRow)}
              colTitleClassName={cls.formLabelCol}
              colChildrenClassName={clsx(cls.formValueCol, cls.formMainValue)}
            >
              <TextInput
                source="name"
                label={false}
                required
                inputProps={{ maxLength: 250 }}
              />
            </FormRow>
            <FormRow
              title="Phone:"
              width={mainColGrid}
              rowClassName={clsx(cls.formRow, cls.mainFormRow)}
              colTitleClassName={cls.formLabelCol}
              colChildrenClassName={clsx(cls.formValueCol, cls.formMainValue)}
            >
              <PhoneFieldConnector />
            </FormRow>
            <FormRow
              title="E-mail:"
              width={mainColGrid}
              rowClassName={clsx(cls.formRow, cls.mainFormRow)}
              colTitleClassName={cls.formLabelCol}
              colChildrenClassName={clsx(cls.formValueCol, cls.formMainValue)}
            >
              <TextInput
                source="email"
                label={false}
                inputProps={{ maxLength: 100 }}
              />
            </FormRow>
            <FormRow
              title="Risk Grade:"
              width={mainColGrid}
              rowClassName={clsx(cls.formRow, cls.mainFormRow)}
              colTitleClassName={cls.formLabelCol}
              colChildrenClassName={clsx(cls.formValueCol, cls.formMainValue)}
            >
              <SelectInput
                label={false}
                source="tier"
                choices={dealerTiersChoices}
              />
            </FormRow>
            <FormRow
              title="Zip Code:"
              width={mainColGrid}
              rowClassName={clsx(cls.formRow, cls.mainFormRow)}
              colTitleClassName={cls.formLabelCol}
              colChildrenClassName={clsx(cls.formValueCol, cls.formMainValue)}
            >
              <TextInput
                source="postalCode"
                label={false}
                inputProps={{ maxLength: 100 }}
              />
            </FormRow>
            <FormRow
              title="State:"
              width={mainColGrid}
              rowClassName={clsx(cls.formRow, cls.mainFormRow)}
              colTitleClassName={cls.formLabelCol}
              colChildrenClassName={clsx(cls.formValueCol, cls.formMainValue)}
            >
              <SelectArrayInput
                label={false}
                source="states"
                choices={states.map(state => ({ id: state.name, name: state.name }))}
              />
            </FormRow>

            <div className={cls.fieldset}>
              <span>Business Data</span>
            </div>

            <FormRow
              title="Status:"
              width={mainColGrid}
              rowClassName={clsx(cls.formRow, cls.mainFormRow)}
              colTitleClassName={cls.formLabelCol}
              colChildrenClassName={clsx(cls.formValueCol, cls.formMainValue)}
            >
              <SelectInput
                label={false}
                source="statusId"
                choices={statuses}
              />
            </FormRow>
            <FormRow
              title="Owner:"
              width={mainColGrid}
              rowClassName={clsx(cls.formRow, cls.mainFormRow)}
              colTitleClassName={cls.formLabelCol}
              colChildrenClassName={clsx(cls.formValueCol, cls.formMainValue)}
            >
              <SelectInput
                label={false}
                source="ownerId"
                choices={owners}
              />
            </FormRow>
            <FormRow
              title="Type:"
              width={mainColGrid}
              rowClassName={clsx(cls.formRow, cls.mainFormRow)}
              colTitleClassName={cls.formLabelCol}
              colChildrenClassName={clsx(cls.formValueCol, cls.formMainValue)}
            >
              <SelectInput
                label={false}
                source="priorityId"
                choices={types}
              />
            </FormRow>
            <FormRow
              title="Farmer Count:"
              width={mainColGrid}
              rowClassName={clsx(cls.formRow, cls.mainFormRow)}
              colTitleClassName={cls.formLabelCol}
              colChildrenClassName={clsx(cls.formValueCol, cls.formMainValue)}
            >
              <TextInput
                source="farmerCount"
                label={false}
                inputProps={{ maxLength: 100 }}
              />
            </FormRow>
            <FormRow
              title="Supplier Loan:"
              width={mainColGrid}
              rowClassName={clsx(cls.formRow, cls.mainFormRow)}
              colTitleClassName={cls.formLabelCol}
              colChildrenClassName={clsx(cls.formValueCol, cls.formMainValue)}
            >
              <SelectInput
                label={false}
                source="supplierLoan"
                choices={dealerSupplierLoanChoices}
              />
            </FormRow>
            <FormRow
              title="Payment Type:"
              width={mainColGrid}
              rowClassName={clsx(cls.formRow, cls.mainFormRow)}
              colTitleClassName={cls.formLabelCol}
              colChildrenClassName={clsx(cls.formValueCol, cls.formMainValue)}
            >
              <SelectInput
                label={false}
                source="paymentType"
                choices={dealerPaymentType}
              />
            </FormRow>
            <FormRow
              title="Close Date:"
              width={mainColGrid}
              rowClassName={clsx(cls.formRow, cls.mainFormRow)}
              colTitleClassName={cls.formLabelCol}
              colChildrenClassName={clsx(cls.formValueCol, cls.formMainValue)}
            >
              <AntdDateInput
                source="closeDate"
                label=""
              />
            </FormRow>
          </div>

          <div className={cls.col}>
            <div className={cls.fieldset}>
              <span>Financial Data</span>
            </div>

            <FormRow
              title="Billing Name:"
              width={mainColGrid}
              rowClassName={clsx(cls.formRow, cls.mainFormRow)}
              colTitleClassName={cls.formLabelCol}
              colChildrenClassName={clsx(cls.formValueCol, cls.formMainValue)}
            >
              <TextInput
                source="billingName"
                label={false}
                inputProps={{ maxLength: 100 }}
              />
            </FormRow>
            <FormRow
              title="RFC:"
              width={mainColGrid}
              rowClassName={clsx(cls.formRow, cls.mainFormRow)}
              colTitleClassName={cls.formLabelCol}
              colChildrenClassName={clsx(cls.formValueCol, cls.formMainValue)}
            >
              <RFCFieldConnector />
            </FormRow>
            <FormRow
              title="CIEC:"
              width={mainColGrid}
              rowClassName={clsx(cls.formRow, cls.mainFormRow)}
              colTitleClassName={cls.formLabelCol}
              colChildrenClassName={clsx(cls.formValueCol, cls.formMainValue)}
            >
              <TextInput
                source="ciec"
                label={false}
                inputProps={{ maxLength: 8 }}
                validate={validateCIEC}
              />
            </FormRow>
            <FormRow
              title="Legal Rep.:"
              width={mainColGrid}
              rowClassName={clsx(cls.formRow, cls.mainFormRow)}
              colTitleClassName={cls.formLabelCol}
              colChildrenClassName={clsx(cls.formValueCol, cls.formMainValue)}
            >
              <LegalRepConnector />
            </FormRow>

            <div className={cls.fieldset}>
              <span>Marketing Data</span>
            </div>

            <FormRow
              title="Activation:"
              width={mainColGrid}
              rowClassName={clsx(cls.formRow, cls.mainFormRow)}
              colTitleClassName={cls.formLabelCol}
              colChildrenClassName={clsx(cls.formValueCol, cls.formMainValue)}
            >
              <SelectInput
                label={false}
                source="activationId"
                choices={activations}
              />
            </FormRow>
            <FormRow
              title="Channel:"
              width={mainColGrid}
              rowClassName={clsx(cls.formRow, cls.mainFormRow)}
              colTitleClassName={cls.formLabelCol}
              colChildrenClassName={clsx(cls.formValueCol, cls.formMainValue)}
            >
              <SelectInput
                source="source"
                choices={sources}
                label={false}
                inputProps={{ maxLength: 100 }}
              />
            </FormRow>
            <FormRow
              title="Facebook:"
              width={mainColGrid}
              rowClassName={clsx(cls.formRow, cls.mainFormRow)}
              colTitleClassName={cls.formLabelCol}
              colChildrenClassName={clsx(cls.formValueCol, cls.formMainValue)}
            >
              <TextInput
                source="facebook"
                label={false}
                inputProps={{ maxLength: 100 }}
                validate={validateURL}
              />
            </FormRow>
            <FormRow
              title="Linkedin:"
              width={mainColGrid}
              rowClassName={clsx(cls.formRow, cls.mainFormRow)}
              colTitleClassName={cls.formLabelCol}
              colChildrenClassName={clsx(cls.formValueCol, cls.formMainValue)}
            >
              <TextInput
                source="linkedin"
                label={false}
                inputProps={{ maxLength: 100 }}
                validate={validateURL}
              />
            </FormRow>
            <FormRow
              title="Instagram:"
              width={mainColGrid}
              rowClassName={clsx(cls.formRow, cls.mainFormRow)}
              colTitleClassName={cls.formLabelCol}
              colChildrenClassName={clsx(cls.formValueCol, cls.formMainValue)}
            >
              <TextInput
                source="instagram"
                label={false}
                inputProps={{ maxLength: 100 }}
                validate={validateURL}
              />
            </FormRow>
            <FormRow
              title="Website:"
              width={mainColGrid}
              rowClassName={clsx(cls.formRow, cls.mainFormRow)}
              colTitleClassName={cls.formLabelCol}
              colChildrenClassName={clsx(cls.formValueCol, cls.formMainValue)}
            >
              <TextInput
                source="website"
                label={false}
                inputProps={{ maxLength: 100 }}
                validate={validateURL}
              />
            </FormRow>
            <FormRow
              title="Veritrade:"
              width={mainColGrid}
              rowClassName={clsx(cls.formRow, cls.mainFormRow)}
              colTitleClassName={cls.formLabelCol}
              colChildrenClassName={clsx(cls.formValueCol, cls.formMainValue)}
            >
              <TextInput
                source="veritrade"
                label={false}
                inputProps={{ maxLength: 100 }}
                validate={validateURL}
              />
            </FormRow>
            <FormRow
              title="Verqor RR:"
              width={mainColGrid}
              rowClassName={clsx(cls.formRow, cls.mainFormRow)}
              colTitleClassName={cls.formLabelCol}
              colChildrenClassName={clsx(cls.formValueCol, cls.formMainValue)}
            >
              <VerqorRRInput
                dealerType={dealerType}
                statuses={statuses}
              />
            </FormRow>
          </div>
        </div>

        <UniqueCrops crops={crops} record={record} states={states} />

        <DealerContacts record={record} />

        <Footer formValues={record} />
      </SimpleForm>
    </div>
  );
};
